import { Controller } from "@hotwired/stimulus";

// Episode Accordion Controller - Manages collapsible episode rows for mobile
export default class extends Controller {
  static targets = ["row", "content", "trigger"];
  static values = {
    expandedId: String,
  };

  connect() {
    this.initializeRows();
  }

  initializeRows() {
    // Ensure all rows start collapsed
    this.rowTargets.forEach((row) => {
      this.collapseRow(row, false);
    });
  }

  // Toggle a specific episode row
  toggleRow(event) {
    event.preventDefault();

    const clickedRow = event.currentTarget.closest(
      '[data-episode-accordion-target="row"]'
    );
    const episodeId = clickedRow.dataset.episodeId;

    // If clicking the same row that's already expanded, collapse it
    if (this.expandedIdValue === episodeId) {
      this.collapseRow(clickedRow, true);
      this.expandedIdValue = "";
    } else {
      // Collapse any currently expanded row
      if (this.expandedIdValue) {
        const currentlyExpanded = this.findRowByEpisodeId(this.expandedIdValue);
        if (currentlyExpanded) {
          this.collapseRow(currentlyExpanded, true);
        }
      }

      // Expand the clicked row
      this.expandRow(clickedRow, true);
      this.expandedIdValue = episodeId;
    }
  }

  // Expand a row with optional animation
  expandRow(row, animate = true) {
    const content = row.querySelector(
      '[data-episode-accordion-target="content"]'
    );
    if (!content) return;

    if (animate) {
      // Set initial state for animation
      content.classList.remove("hidden");
      content.style.height = "0px";
      content.style.opacity = "0";
      content.style.overflow = "hidden";

      // Force reflow to ensure styles are applied
      requestAnimationFrame(() => {
        // Get the natural height
        const naturalHeight = content.scrollHeight;

        // Animate to full height
        content.style.transition =
          "height 300ms ease-in-out, opacity 300ms ease-in-out";
        content.style.height = naturalHeight + "px";
        content.style.opacity = "1";

        // Clean up after animation
        setTimeout(() => {
          content.style.height = "auto";
          content.style.transition = "";
          content.style.overflow = "";
        }, 300);
      });
    } else {
      content.classList.remove("hidden");
      content.style.height = "auto";
      content.style.opacity = "1";
    }

    // Update row state
    row.setAttribute("data-expanded", "true");
    row.classList.add("bg-slate-750");

    // Update trigger state if it exists
    const trigger = row.querySelector(
      '[data-episode-accordion-target="trigger"]'
    );
    if (trigger) {
      trigger.setAttribute("aria-expanded", "true");
    }

    // Rotate chevron
    const chevron = row.querySelector(".episode-accordion-chevron");
    if (chevron) {
      chevron.style.transform = "rotate(180deg)";
    }
  }

  // Collapse a row with optional animation
  collapseRow(row, animate = true) {
    const content = row.querySelector(
      '[data-episode-accordion-target="content"]'
    );
    if (!content) return;

    if (animate && !content.classList.contains("hidden")) {
      // Set up for animation
      content.style.height = content.scrollHeight + "px";
      content.style.transition =
        "height 300ms ease-in-out, opacity 300ms ease-in-out";

      // Force reflow
      content.offsetHeight;

      // Animate to collapsed state
      content.style.height = "0px";
      content.style.opacity = "0";

      // Hide after animation
      setTimeout(() => {
        content.classList.add("hidden");
        content.style.transition = "";
        content.style.height = "";
        content.style.opacity = "";
      }, 300);
    } else {
      content.classList.add("hidden");
      content.style.height = "";
      content.style.opacity = "";
    }

    // Update row state
    row.setAttribute("data-expanded", "false");
    row.classList.remove("bg-slate-750");

    // Update trigger state if it exists
    const trigger = row.querySelector(
      '[data-episode-accordion-target="trigger"]'
    );
    if (trigger) {
      trigger.setAttribute("aria-expanded", "false");
    }

    // Reset chevron
    const chevron = row.querySelector(".episode-accordion-chevron");
    if (chevron) {
      chevron.style.transform = "rotate(0deg)";
    }
  }

  // Helper method to find row by episode ID
  findRowByEpisodeId(episodeId) {
    return this.rowTargets.find((row) => row.dataset.episodeId === episodeId);
  }

  // Prevent toggle when clicking on checkbox area
  preventToggle(event) {
    event.stopPropagation();
    console.log("🚫 Prevented toggle for checkbox");
  }
}
